// src/data/friendshipModulesMetadata.js
// Card metadata for the modules grid - presentation only.
//
// ⚠️ duration is NOT written here. It is derived from the module content
// itself, so a card can never disagree with the module behind it. Cards used
// to claim a different length from the module's own header, because the two
// lists were maintained by hand and drifted apart. (Modules carry no points:
// those went with the rest of the gamification in October 2026.)
import { friendshipModules } from './modules/friendship-modules-index';
import { getMicroSessions, sessionMinutes } from '../utils/moduleShape';

const byId = new Map(friendshipModules.map((m) => [m.id, m]));

// Sum of the module's micro-session durations, which is what a reader
// actually spends in it.
const durationFor = (id) => {
  const module = byId.get(id);
  if (!module) return null;
  // Both module formats: legacy `content.microSessions[].duration` strings
  // and block-format `microSessions[].estimatedMinutes`.
  const minutes = getMicroSessions(module).reduce(
    (total, session) => total + sessionMinutes(session),
    0
  );
  return minutes ? `${minutes} min` : module.duration || null;
};

const withDerivedFields = (modules) =>
  modules.map((entry) => ({
    ...entry,
    duration: durationFor(entry.id) ?? entry.duration,
  }));

const friendshipModulesMeta = [
  {
    id: 1,
    title: 'Your Social Brain',
    subtitle: 'Understanding how your brain handles friendship',
    description: 'Discover why some friendships feel effortless while others drain you, and learn to work with your social brain instead of against it.',
    level: 'Foundation',
    category: 'Understanding',
    series: 'Friendship Navigator',
    coolFactor: 'Like getting the manual for your social operating system',
    image: '/images/friendship-01-social-brain.png',
    imageAlt: 'Brain illustration showing social connections and neural pathways',
  },
  {
    id: 2,
    title: 'Group Dynamics & Roles',
    subtitle: 'Decode the hidden patterns in friend groups',
    description: 'Every friend group has invisible rules and roles. Learn to read the room and navigate group dynamics without losing yourself.',
    level: 'Foundation',
    category: 'Understanding',
    series: 'Friendship Navigator',
    coolFactor: 'X-ray vision for friend group politics',
    image: '/images/friendship-02-roles.png',
    imageAlt: 'Diverse friend group with different roles highlighted',
  },
  {
    id: 3,
    title: 'Healthy Conflict Skills',
    subtitle: 'Turn disagreements into deeper connection',
    description: 'Conflict doesn\'t have to mean drama. Master the art of disagreeing without destroying the friendship.',
    level: 'Essential',
    category: 'Core Skills',
    series: 'Friendship Navigator',
    coolFactor: 'Conflict resolution that actually strengthens bonds',
    image: '/images/friendship-03-conflict.png',
    imageAlt: 'Two friends having a respectful conversation',
  },
  {
    id: 4,
    title: 'Digital Drama Defence',
    subtitle: 'Navigate online friendship challenges',
    description: 'From group chats to social media, learn to handle digital friendship drama before it spirals out of control.',
    level: 'Essential',
    category: 'Core Skills',
    series: 'Friendship Navigator',
    coolFactor: 'Digital friendship armour for the modern world',
    image: '/images/friendship-04-digital-drama.png',
    imageAlt: 'Phone screen showing healthy vs toxic digital interactions',
  },
  {
    id: 5,
    title: 'Friendship Endings & Grief',
    subtitle: 'When friendships change or end',
    description: 'Not all friendships last forever, and that\'s okay. Learn to process friendship loss and know when it\'s time to let go.',
    level: 'Advanced',
    category: 'Emotional Mastery',
    series: 'Friendship Navigator',
    coolFactor: 'Healing and wisdom for life\'s friendship transitions',
    image: '/images/friendship-05-endings.png',
    imageAlt: 'Person reflecting peacefully by a sunset, representing healthy endings',
  },
  {
    id: 6,
    title: 'Finding Your People',
    subtitle: 'Attract genuine, lasting friendships',
    description: 'Stop settling for surface-level connections. Learn to attract and build the deep, authentic friendships you actually want.',
    level: 'Advanced',
    category: 'Emotional Mastery',
    series: 'Friendship Navigator',
    coolFactor: 'Magnetism for the friendships that truly fit you',
    image: '/images/friendship-06-your-people.png',
    imageAlt: 'Person surrounded by genuine, diverse friend connections',
  },
  {
    id: 7,
    title: 'Social Confidence Code',
    subtitle: 'Authentic confidence in any social situation',
    description: 'Build genuine social confidence that doesn\'t depend on everyone liking you. Be yourself, magnetically.',
    level: 'Advanced',
    category: 'Emotional Mastery',
    series: 'Friendship Navigator',
    coolFactor: 'Magnetic authenticity that attracts the right people',
    image: '/images/friendship-07-social-confidence.png',
    imageAlt: 'Confident person engaging naturally in social settings',
  },
  {
    id: 8,
    title: 'Friendship Mastery Integration',
    subtitle: 'Putting it all together',
    description: 'Integrate everything you\'ve learned into a sustainable approach to friendship that works for your real life.',
    level: 'Mastery',
    category: 'Advanced Mastery',
    series: 'Friendship Navigator',
    coolFactor: 'Master level friendship skills for life',
    image: '/images/friendship-08-mastery.png',
    imageAlt: 'Person confidently navigating various friendship scenarios',
  },
];

export const friendshipModulesData = withDerivedFields(friendshipModulesMeta);
